import React from 'react';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import APIClient from '../util/APIClient';
import AuthenticationContext from '../contexts/AuthenticationContext';

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
      userPoolClientId: process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID,
    },
  },
});

export default function useAuth() {
  return React.useContext(AuthenticationContext);
}

export function AuthenticationProvider(props) {
  const { children } = props;
  const [authState, setAuthState] = React.useState({});

  function updateCurrentAuthenticatedUser() {
    fetchAuthSession()
      .then((session) => {
        const idToken = session?.tokens?.idToken.toString();
        if (!idToken) {
          throw new Error('ID Token not found!');
        }
        APIClient.defaults.headers.common.Authorization = `Bearer ${idToken}`;
        APIClient.get('/account', {
          params: {
            includeGroups: true,
            includeTeams: true,
            includeTeamGoals: true,
          },
        }).then((result) => {
          const groups = result.data.groups.map((g) => g.label);
          const userLastVersion = process.env.REACT_APP_DISPLAY_RELEASE_NOTES ? '0.0.0' : result.data.lastVersion;
          setAuthState({
            idToken: idToken,
            currentUser: result.data,
            groups,
            isSignedIn: true,
            isAuthenticationComplete: true,
            userLastVersion: userLastVersion || null,
          });
        });
      })
      .catch(() => {
        APIClient.defaults.headers.common.Authorization = undefined;
        setAuthState({
          idToken: null,
          currentUser: null,
          groups: [],
          isSignedIn: false,
          isAuthenticationComplete: true,
          userLastVersion: null,
        });
      });
  }

  React.useEffect(() => {
    function onAuthEvent({ payload }) {
      switch (payload.event) {
        case 'signedIn':
        case 'tokenRefresh':
          updateCurrentAuthenticatedUser();
          break;
        case 'signedOut':
        case 'tokenRefresh_failure':
          setAuthState({
            idToken: null,
            currentUser: null,
            groups: [],
            isSignedIn: false,
            isAuthenticationComplete: true,
            userLastVersion: null,
          });
          APIClient.defaults.headers.common.Authorization = undefined;
          break;
      }
    }

    Hub.listen('auth', onAuthEvent);
    updateCurrentAuthenticatedUser();
  }, []);

  return <AuthenticationContext.Provider value={authState}>{children}</AuthenticationContext.Provider>;
}
